/**
 * @fileoverview Hook for managing What's New / App Updates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type AppUpdate = Tables<'app_updates'>;
type AppUpdateInsert = TablesInsert<'app_updates'>;
type AppUpdateUpdate = TablesUpdate<'app_updates'>;

export function useAppUpdates() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch published updates
  const { data: updates = [], isLoading } = useQuery({
    queryKey: ['app-updates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_updates')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Fetch user's read updates
  const { data: readUpdateIds = [] } = useQuery({
    queryKey: ['app-update-reads', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('app_update_reads')
        .select('update_id')
        .eq('user_id', user.id);

      if (error) throw error;
      return (data || []).map((r) => r.update_id);
    },
    enabled: !!user,
  });

  // Calculate unread count
  const unreadCount = updates.filter(u => !readUpdateIds.includes(u.id)).length;

  // Mark update as read
  const markAsReadMutation = useMutation({
    mutationFn: async (updateId: string) => {
      if (!user) return;
      
      await supabase
        .from('app_update_reads')
        .insert({ user_id: user.id, update_id: updateId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['app-update-reads', user?.id] });
    },
  });

  // Mark all as read
  const markAllAsRead = async () => {
    if (!user) return;
    
    const unreadUpdates = updates.filter(u => !readUpdateIds.includes(u.id));
    for (const update of unreadUpdates) {
      await markAsReadMutation.mutateAsync(update.id);
    }
  };

  return {
    updates,
    isLoading,
    unreadCount,
    readUpdateIds,
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead,
  };
}

// Admin hook for managing updates
export function useAdminAppUpdates() {
  const queryClient = useQueryClient();

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ['admin-app-updates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_updates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (update: AppUpdateInsert) => {
      const { data, error } = await supabase
        .from('app_updates')
        .insert(update)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-app-updates'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...update }: AppUpdateUpdate & { id: string }) => {
      const { error } = await supabase
        .from('app_updates')
        .update(update)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-app-updates'] });
      queryClient.invalidateQueries({ queryKey: ['app-updates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('app_updates')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-app-updates'] });
    },
  });

  return {
    updates,
    isLoading,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}
